import React, { useState, useContext } from "react";
import { useParams } from "react-router-dom";
import lscache from "lscache";
import useFetchVideo from "../../../../Hooks/Content/Watch/VideoFeed/useFetchVideoById";
import useFetchVideoPeakInfo from "../../../../Hooks/Content/Watch/VideoSingle/useFetchVideoPeakInfo";
import useVideoPlayback from "../../../../Hooks/Content/Watch/VideoSingle/useVideoPlayback";
import VideoPlayer from "../../../../Components/Layouts/Content/Watch/VideoSingle/VideoPlayer/VideoPlayer";
import VideoPrimaryInformation from "../../../../Components/Layouts/Content/Watch/VideoSingle/VideoPrimaryInformation";
import VideoRelatedVideos from "../../../../Components/Layouts/Content/Watch/VideoSingle/VideoRelatedVideos";
import VideoComments from "../../../../Components/VideoComments";
import VideoAccessOverlay from "../../../../Components/Layouts/Content/Watch/VideoSingle/VideoAccessOverlay";
import { UserAuthenticationContext } from "../../../../Context/UserAuthenticationContext";
import { VideoPlayerSettingsContext } from "../../../../Context/VideoPlayerSettingsContext";
import VideoDTO from "../../../../Models/DTOs/VideoDTO";

function WatchVideo() {
    const { id } = useParams<string>();
    const videoId = id && /^\d+$/.test(id) ? Number(id) : undefined;
    const { userData, doesAuthTokenExist } = useContext(UserAuthenticationContext);
    const { isTheatreMode, setIsTheatreMode } = useContext(VideoPlayerSettingsContext);
    const [isCommentInputFocused, setIsCommentInputFocused] = useState(false);
    // Marketing completion can hide the email form; it never supplies playback permission.
    const [freeViewComplete, setFreeViewComplete] = useState(() => !!lscache.get("freeViewAllowed"));
    const { video, videoError, retry: retryVideo } = useFetchVideo(videoId, "GB");
    const videoData = video && video.Id === videoId ? video : undefined;
    const { peak, updatePeak } = useFetchVideoPeakInfo(videoData?.Id);
    const playback = useVideoPlayback(videoId);
    const grant = playback.state.status === "ready" && playback.state.decision.allowed
        ? playback.state.decision : null;
    const needsFreeViewEmail = !!grant && !!videoData?.IsFreeView && !doesAuthTokenExist() && !freeViewComplete;
    const playbackAllowed = !!videoData && !!grant && !needsFreeViewEmail;

    if (!videoId || !Number.isSafeInteger(videoId) || videoError) {
        return <div className="page--watch-video" role="alert">This video is currently unavailable.
            {!!videoId && <button type="button" onClick={() => { retryVideo(); playback.retry(); }}>Try again</button>}
        </div>;
    }
    const displayVideo = videoData ?? {} as VideoDTO;
    return <div className="page--watch-video">
        <div className={`page__wrapper ${isTheatreMode ? "is-theatre-mode" : "is-not-theatre-mode"}`}>
            <VideoPlayer
                key={`${playback.scope}:${playbackAllowed}`}
                video={videoData}
                src={playbackAllowed ? grant?.url : undefined}
                autoPlay={playbackAllowed}
                playbackAllowed={playbackAllowed}
                isTheatreMode={isTheatreMode}
                setIsTheatreMode={setIsTheatreMode}
                peak={peak}
                updatePeak={updatePeak}
                isCommentInputFocused={isCommentInputFocused}
            />
            <div className="watch__column watch__column-info">
                <VideoPrimaryInformation video={displayVideo} isVideoLoading={!videoData} doesAuthTokenExist={doesAuthTokenExist()} />
                <VideoComments videoId={videoData?.Id} onComposerFocusChange={setIsCommentInputFocused} />
            </div>
            <div className="watch__column watch__column-related">
                <VideoRelatedVideos videoId={videoId} videoCategoryId={videoData?.VideoCategoriesId}
                    additionalCategories={displayVideo.AdditionalCategories} parentContentStillLoading={!videoData}
                    userData={userData} doesAuthTokenExist={doesAuthTokenExist} />
            </div>
        </div>
        {!playbackAllowed && <VideoAccessOverlay playback={playback.state} needsFreeViewEmail={needsFreeViewEmail}
            onRetry={playback.retry} onFreeViewComplete={() => {
                setFreeViewComplete(true);
                lscache.set("freeViewAllowed", true);
                playback.retry();
            }} />}
    </div>;
}
export default WatchVideo;
